export type VehicleCameraConfig = {
  liveReload: {
    enabled: boolean
    pollIntervalMs: number
  }
  chaseRoad: {
    distanceM: number
    heightM: number
    targetHeightM: number
    lookAheadMinimumM: number
    lookAheadMaximumM: number
    lookAheadFullSpeedMps: number
    baseFovDegrees: number
    maximumFovDegrees: number
    fovFullSpeedMps: number
    velocityFrame: number
    velocityFrameTransientDampingSeconds: number
    gforceTranslationMaxDistanceM: number
    gforceTranslationSmoothingSeconds: number
    verticalPositionSpringRate: number
    verticalPositionDamping: number
    topSpeedVerticalPositionM: number
    topSpeedVerticalPositionFullSpeedKph: number
    topSpeedVerticalPositionSmoothingSeconds: number
    rightStickYawDegrees: number
    rightStickDeadzone: number
    rightStickResponse: number
    rightStickReturnResponse: number
  }
}

export const defaultVehicleCameraConfig: VehicleCameraConfig = {
  liveReload: {
    enabled: true,
    pollIntervalMs: 250,
  },
  chaseRoad: {
    distanceM: 5.6,
    heightM: 1.7,
    targetHeightM: 0.85,
    lookAheadMinimumM: 3,
    lookAheadMaximumM: 10,
    lookAheadFullSpeedMps: 40,
    baseFovDegrees: 52,
    maximumFovDegrees: 61,
    fovFullSpeedMps: 45,
    velocityFrame: 0.25,
    velocityFrameTransientDampingSeconds: 0.18,
    gforceTranslationMaxDistanceM: 0.35,
    gforceTranslationSmoothingSeconds: 0.12,
    verticalPositionSpringRate: 45,
    verticalPositionDamping: 12,
    topSpeedVerticalPositionM: 1.05,
    topSpeedVerticalPositionFullSpeedKph: 600,
    topSpeedVerticalPositionSmoothingSeconds: 0.65,
    rightStickYawDegrees: 180,
    rightStickDeadzone: 0.14,
    rightStickResponse: 12,
    rightStickReturnResponse: 8,
  },
}

type IniDocument = Map<string, Map<string, string>>

function parseIni(text: string) {
  const document: IniDocument = new Map()
  let section = ''
  for (const [index, sourceLine] of text.split(/\r?\n/).entries()) {
    const line = sourceLine.trim()
    if (!line || line.startsWith(';') || line.startsWith('#')) continue
    const sectionMatch = /^\[([^\]]+)\]$/.exec(line)
    if (sectionMatch) {
      section = sectionMatch[1].trim().toUpperCase()
      if (!section) throw new Error(`Camera INI line ${index + 1}: empty section name`)
      if (!document.has(section)) document.set(section, new Map())
      continue
    }
    const separator = line.indexOf('=')
    if (!section || separator < 1) throw new Error(`Camera INI line ${index + 1}: expected key=value inside a section`)
    const key = line.slice(0, separator).trim().toLowerCase()
    const rawValue = line.slice(separator + 1).split(/[;#]/, 1)[0].trim()
    if (!key || !rawValue) throw new Error(`Camera INI line ${index + 1}: empty key or value`)
    document.get(section)!.set(key, rawValue)
  }
  return document
}

function raw(document: IniDocument, section: string, key: string) {
  return document.get(section)?.get(key)
}

function numberValue(document: IniDocument, section: string, key: string, fallback: number, minimum: number, maximum: number) {
  const source = raw(document, section, key)
  if (source === undefined) return fallback
  const value = Number(source)
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new Error(`Camera INI [${section}] ${key} must be between ${minimum} and ${maximum}`)
  }
  return value
}

function booleanValue(document: IniDocument, section: string, key: string, fallback: boolean) {
  const source = raw(document, section, key)
  if (source === undefined) return fallback
  if (/^(true|yes|on|1)$/i.test(source)) return true
  if (/^(false|no|off|0)$/i.test(source)) return false
  throw new Error(`Camera INI [${section}] ${key} must be true or false`)
}

export function parseVehicleCameraIni(text: string, previous = defaultVehicleCameraConfig): VehicleCameraConfig {
  const document = parseIni(text)
  const live = previous.liveReload
  const chase = previous.chaseRoad
  const next: VehicleCameraConfig = {
    liveReload: {
      enabled: booleanValue(document, 'LIVE_RELOAD', 'enabled', live.enabled),
      pollIntervalMs: numberValue(document, 'LIVE_RELOAD', 'poll_interval_ms', live.pollIntervalMs, 100, 10_000),
    },
    chaseRoad: {
      distanceM: numberValue(document, 'CHASE_ROAD', 'distance_m', chase.distanceM, 1, 30),
      heightM: numberValue(document, 'CHASE_ROAD', 'height_m', chase.heightM, 0.2, 20),
      targetHeightM: numberValue(document, 'CHASE_ROAD', 'target_height_m', chase.targetHeightM, 0, 5),
      lookAheadMinimumM: numberValue(document, 'CHASE_ROAD', 'look_ahead_minimum_m', chase.lookAheadMinimumM, 0, 2_000),
      lookAheadMaximumM: numberValue(document, 'CHASE_ROAD', 'look_ahead_maximum_m', chase.lookAheadMaximumM, 0, 5_000),
      lookAheadFullSpeedMps: numberValue(document, 'CHASE_ROAD', 'look_ahead_full_speed_mps', chase.lookAheadFullSpeedMps, 1, 150),
      baseFovDegrees: numberValue(document, 'CHASE_ROAD', 'base_fov_degrees', chase.baseFovDegrees, 20, 100),
      maximumFovDegrees: numberValue(document, 'CHASE_ROAD', 'maximum_fov_degrees', chase.maximumFovDegrees, 20, 120),
      fovFullSpeedMps: numberValue(document, 'CHASE_ROAD', 'fov_full_speed_mps', chase.fovFullSpeedMps, 1, 150),
      velocityFrame: numberValue(document, 'CHASE_ROAD', 'velocity_frame', chase.velocityFrame, 0, 1),
      velocityFrameTransientDampingSeconds: numberValue(document, 'CHASE_ROAD', 'velocity_frame_transient_damping_seconds', chase.velocityFrameTransientDampingSeconds, 0, 10),
      gforceTranslationMaxDistanceM: numberValue(document, 'CHASE_ROAD', 'gforce_translation_max_distance_m', chase.gforceTranslationMaxDistanceM, 0, 5),
      gforceTranslationSmoothingSeconds: numberValue(document, 'CHASE_ROAD', 'gforce_translation_smoothing_seconds', chase.gforceTranslationSmoothingSeconds, 0, 10),
      verticalPositionSpringRate: numberValue(document, 'CHASE_ROAD', 'vertical_position_spring_rate', chase.verticalPositionSpringRate, 0, 500),
      verticalPositionDamping: numberValue(document, 'CHASE_ROAD', 'vertical_position_damping', chase.verticalPositionDamping, 0, 100),
      topSpeedVerticalPositionM: numberValue(document, 'CHASE_ROAD', 'top_speed_vertical_position_m', chase.topSpeedVerticalPositionM, 0.1, 20),
      topSpeedVerticalPositionFullSpeedKph: numberValue(document, 'CHASE_ROAD', 'top_speed_vertical_position_full_speed_kph', chase.topSpeedVerticalPositionFullSpeedKph, 1, 2_000),
      topSpeedVerticalPositionSmoothingSeconds: numberValue(document, 'CHASE_ROAD', 'top_speed_vertical_position_smoothing_seconds', chase.topSpeedVerticalPositionSmoothingSeconds, 0, 10),
      rightStickYawDegrees: numberValue(document, 'CHASE_ROAD', 'right_stick_yaw_degrees', chase.rightStickYawDegrees, 0, 180),
      rightStickDeadzone: numberValue(document, 'CHASE_ROAD', 'right_stick_deadzone', chase.rightStickDeadzone, 0, 0.95),
      rightStickResponse: numberValue(document, 'CHASE_ROAD', 'right_stick_response', chase.rightStickResponse, 0, 60),
      rightStickReturnResponse: numberValue(document, 'CHASE_ROAD', 'right_stick_return_response', chase.rightStickReturnResponse, 0, 60),
    },
  }
  if (next.chaseRoad.lookAheadMaximumM < next.chaseRoad.lookAheadMinimumM) {
    throw new Error('Camera INI look_ahead_maximum_m cannot be less than look_ahead_minimum_m')
  }
  if (next.chaseRoad.maximumFovDegrees < next.chaseRoad.baseFovDegrees) {
    throw new Error('Camera INI maximum_fov_degrees cannot be less than base_fov_degrees')
  }
  return next
}
